import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Target,
  TrendingUp,
  Clock,
  Compass,
  Brain,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Zap,
  ChevronRight,
  RefreshCw,
  Sparkles,
  ArrowRight,
  Shield,
  Activity
} from "lucide-react";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

/**
 * Deal Command Center - AI-Powered Execution Cockpit
 * 
 * Displays:
 * - Deal Confidence Score (0-100)
 * - Projected Profit 
 * - Estimated Time-to-Close
 * - Recommended Strategy
 * - Next Best Action with execution buttons
 */
export default function DealCommandCenter({ 
  deal, 
  guidance, 
  scenarios, 
  onAction,
  className = ""
}) {
  const navigate = useNavigate();
  
  if (!deal || !guidance) return null;
  
  // Calculate comprehensive deal confidence score
  const calculateConfidenceScore = () => {
    let score = 50; // Base score
    const factors = [];
    
    // 70% Rule adherence (key REI metric)
    const arv = deal.arv || deal.exit_price || 0;
    const purchase = deal.asking_price || 0;
    const rehab = deal.rehab_estimate || 0;
    
    if (arv > 0 && purchase > 0) {
      const maxAllowable = arv * 0.7 - rehab;
      const purchasePercent = purchase / arv;
      
      if (purchase <= maxAllowable) {
        score += 20; // Meets 70% rule
        factors.push({ label: "Meets 70% Rule", impact: "+20", positive: true });
      } else if (purchasePercent <= 0.8) {
        score += 10;
        factors.push({ label: "Within 80% ARV", impact: "+10", positive: true });
      } else {
        score -= 10;
        factors.push({ label: "Over 80% ARV", impact: "-10", positive: false });
      }
    }
    
    // Profit margin check
    const bestScenario = scenarios?.reduce((best, s) => {
      const profit = s.estimated_profit || s.profit || 0;
      return profit > (best?.estimated_profit || best?.profit || 0) ? s : best;
    }, null);
    
    if (bestScenario) {
      const profit = bestScenario.estimated_profit || bestScenario.profit || 0;
      const roi = bestScenario.roi || 0;
      
      if (profit >= 50000) {
        score += 15;
        factors.push({ label: "$50K+ profit potential", impact: "+15", positive: true });
      } else if (profit >= 25000) {
        score += 10;
        factors.push({ label: "$25K+ profit potential", impact: "+10", positive: true });
      } else if (profit < 10000 && profit > 0) {
        score -= 5;
        factors.push({ label: "Thin profit margin", impact: "-5", positive: false });
      }
      
      if (roi >= 30) {
        score += 10;
        factors.push({ label: "30%+ ROI", impact: "+10", positive: true });
      } else if (roi >= 20) {
        score += 5;
        factors.push({ label: "20%+ ROI", impact: "+5", positive: true });
      }
    }
    
    // Stage progression
    const stageScores = {
      new_lead: 0,
      analyzed: 5,
      underwritten: 10,
      contracted: 15,
      closed: 20
    };
    score += stageScores[deal.pipeline_stage] || 0;
    
    // Risk flags penalty
    const riskCount = guidance.risk_flags?.length || 0;
    if (riskCount > 0) {
      const criticalRisks = guidance.risk_flags.filter(r => r.severity === "critical").length;
      const warningRisks = guidance.risk_flags.filter(r => r.severity === "warning").length;
      score -= criticalRisks * 15;
      score -= warningRisks * 5;
      if (criticalRisks > 0) {
        factors.push({ label: `${criticalRisks} critical risk(s)`, impact: `-${criticalRisks * 15}`, positive: false });
      }
    }
    
    // Data completeness bonus
    const completeness = guidance.readiness_score || 50;
    if (completeness >= 80) {
      score += 5;
      factors.push({ label: "Data complete", impact: "+5", positive: true });
    }
    
    // Clamp score between 0-100
    score = Math.max(0, Math.min(100, score));
    
    return { score, factors };
  };
  
  // Calculate projected profit
  const calculateProjectedProfit = () => {
    const commandCenter = guidance.command_center || {};
    if (commandCenter.projected_profit) {
      return commandCenter.projected_profit;
    }
    
    // Fallback: calculate from scenarios
    const bestScenario = scenarios?.reduce((best, s) => {
      const profit = s.estimated_profit || s.profit || 0;
      return profit > (best?.estimated_profit || best?.profit || 0) ? s : best;
    }, null);
    
    return bestScenario?.estimated_profit || bestScenario?.profit || 0;
  };
  
  // Get time to close estimate
  const getTimeToClose = () => {
    const commandCenter = guidance.command_center || {};
    const days = commandCenter.estimated_days_to_close;
    
    if (days === 0) return "Closed";
    if (days <= 7) return "< 1 week";
    if (days <= 14) return "~2 weeks";
    if (days <= 30) return "~1 month";
    if (days <= 45) return "~6 weeks";
    return `~${Math.ceil(days / 30)} months`;
  };
  
  // Get recommended strategy
  const getRecommendedStrategy = () => {
    const commandCenter = guidance.command_center || {};
    const strategyRec = commandCenter.strategy_recommendation;
    
    if (strategyRec) return strategyRec;
    
    // Fallback: pick best scenario strategy
    const bestScenario = scenarios?.reduce((best, s) => {
      const profit = s.estimated_profit || s.profit || 0;
      return profit > (best?.estimated_profit || best?.profit || 0) ? s : best;
    }, null);
    
    if (bestScenario) {
      const strategyLabels = {
        wholesale: "Wholesale",
        fix_flip: "Fix & Flip",
        subject_to: "Subject-To",
        seller_finance: "Seller Finance",
        wrap: "Wrap Mortgage",
        cash: "Cash Purchase"
      };
      return {
        strategy: bestScenario.strategy,
        label: strategyLabels[bestScenario.strategy] || bestScenario.strategy,
        profit: bestScenario.estimated_profit || bestScenario.profit,
        reasoning: []
      };
    }
    
    return null;
  };
  
  const { score: confidenceScore, factors: confidenceFactors } = calculateConfidenceScore();
  const projectedProfit = calculateProjectedProfit();
  const timeToClose = getTimeToClose();
  const strategy = getRecommendedStrategy();
  const autopilotStatus = guidance.command_center?.autopilot_status || "active";
  const recommendedAction = guidance.recommended_action;
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };
  
  const getConfidenceColor = (score) => {
    if (score >= 70) return { bg: "bg-emerald-500", text: "text-emerald-500", border: "border-emerald-500" };
    if (score >= 50) return { bg: "bg-amber-500", text: "text-amber-500", border: "border-amber-500" };
    return { bg: "bg-red-500", text: "text-red-500", border: "border-red-500" };
  };
  
  const getConfidenceLabel = (score) => {
    if (score >= 80) return "Strong Deal";
    if (score >= 60) return "Solid Deal";
    if (score >= 40) return "Marginal";
    return "High Risk";
  };
  
  const getAutopilotStatusDisplay = (status) => {
    switch (status) {
      case "active":
        return { label: "Active", icon: Activity, color: "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] bg-emerald-500/10" };
      case "waiting_on_input":
        return { label: "Waiting on Input", icon: Clock, color: "text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] bg-amber-500/10" };
      case "risk_detected":
        return { label: "Risk Detected", icon: AlertTriangle, color: "text-red-400 bg-red-500/10" };
      case "ready_to_execute":
        return { label: "Ready to Execute", icon: Zap, color: "text-gold bg-gold/10" };
      case "complete":
        return { label: "Complete", icon: CheckCircle, color: "text-white/70 bg-white/20/10" };
      default:
        return { label: "Unknown", icon: Activity, color: "text-white/70 bg-white/20/10" };
    }
  };
  
  const colors = getConfidenceColor(confidenceScore);
  const autopilot = getAutopilotStatusDisplay(autopilotStatus);
  const AutopilotIcon = autopilot.icon;
  
  const handleActionClick = (action) => {
    if (onAction) {
      onAction(action);
    } else if (action.route) {
      navigate(action.route);
    }
  };
  
  return (
    <div 
      className={`bg-gradient-to-r from-black via-[#0A0A0A] to-black border border-white/[0.08] overflow-hidden ${className}`}
      data-testid="deal-command-center"
    >
      {/* Header Bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#0A0A0A]/80 backdrop-blur-sm/50 border-b border-white/[0.08]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-gold/10 flex items-center justify-center">
            <Brain className="w-4 h-4 text-gold" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white uppercase tracking-wide">Deal Command Center</h3>
            <p className="text-xs text-white/70">AI-Powered Execution Cockpit</p>
          </div>
        </div>
        
        {/* Autopilot Status */}
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${autopilot.color}`}>
          <AutopilotIcon className="w-3.5 h-3.5" />
          <span className="text-xs font-medium">{autopilot.label}</span>
        </div>
      </div>
      
      {/* Main Metrics Grid */}
      <div className="grid grid-cols-4 divide-x divide-white/[0.06]">
        {/* Deal Confidence */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="p-4 cursor-help hover:bg-[#0A0A0A]/80 backdrop-blur-sm/50 transition-all duration-300">
              <div className="flex items-center gap-2 mb-2">
                <Shield className={`w-4 h-4 ${colors.text}`} />
                <span className="text-xs text-white/70 uppercase font-bold">Confidence</span>
              </div>
              <div className="flex items-end gap-2">
                <span className={`text-3xl font-bold font-mono ${colors.text}`}>
                  {confidenceScore}
                </span>
                <span className="text-sm text-white/70 mb-1">/100</span>
              </div>
              <div className={`text-xs ${colors.text} font-medium mt-1`}>
                {getConfidenceLabel(confidenceScore)}
              </div>
              {/* Mini Progress Bar */}
              <div className="mt-2 h-1.5 bg-white/[0.08] rounded-full overflow-hidden">
                <div 
                  className={`h-full ${colors.bg} transition-all duration-500`}
                  style={{ width: `${confidenceScore}%` }}
                />
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="bg-black border-white/[0.08] p-3 max-w-xs">
            <div className="text-sm text-white font-bold mb-2">Confidence Factors</div>
            <div className="space-y-1">
              {confidenceFactors.map((f, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-white/70">{f.label}</span>
                  <span className={f.positive ? "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" : "text-red-400"}>
                    {f.impact}
                  </span>
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
        
        {/* Projected Profit */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-gold" />
            <span className="text-xs text-white/70 uppercase font-bold">Projected Profit</span>
          </div>
          <div className={`text-3xl font-bold font-mono ${projectedProfit >= 0 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-red-400'}`}>
            {formatCurrency(projectedProfit)}
          </div>
          <div className="text-xs text-white/70 mt-1">
            Based on {strategy?.label || "best scenario"}
          </div>
        </div>
        
        {/* Time to Close */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-white/70 uppercase font-bold">Est. Close</span>
          </div>
          <div className="text-3xl font-bold font-mono text-white">
            {timeToClose}
          </div>
          <div className="text-xs text-white/70 mt-1">
            {deal.pipeline_stage?.replace('_', ' ')} stage
          </div>
        </div>
        
        {/* Recommended Strategy */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Compass className="w-4 h-4 text-purple-400" />
            <span className="text-xs text-white/70 uppercase font-bold">Strategy</span>
          </div>
          {strategy ? (
            <>
              <div className="text-xl font-bold text-white">
                {strategy.label}
              </div>
              <div className="text-xs text-white/70 mt-1">
                {strategy.reasoning?.join(" • ") || "Best ROI option"}
              </div>
            </>
          ) : (
            <div className="text-lg text-white/70 italic">
              Run calculator to analyze
            </div>
          )}
        </div>
      </div>
      
      {/* Next Best Action */}
      {recommendedAction && (
        <div className="border-t border-white/[0.08] bg-gradient-to-r from-gold/5 via-transparent to-gold/5">
          <div className="p-4 flex items-center gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded bg-gold/20 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-gold" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gold uppercase font-bold">Next Best Action</span>
                  <Sparkles className="w-3 h-3 text-gold/60" />
                </div>
                <h4 className="text-lg font-bold text-white">{recommendedAction.label}</h4>
                <p className="text-sm text-white/70">{recommendedAction.description}</p>
              </div>
            </div>
            
            {recommendedAction.route && (
              <Button 
                onClick={() => handleActionClick(recommendedAction)}
                className="gold-gradient-btn rounded-none font-bold px-6 py-2.5 flex items-center gap-2"
                data-testid="next-action-btn"
              >
                {recommendedAction.action_type === "advance_stage" ? "Advance Stage" : "Take Action"}
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          {/* Secondary Actions */}
          {guidance.secondary_actions?.length > 0 && (
            <div className="px-4 pb-3 flex items-center gap-2">
              <span className="text-xs text-white/70">Also consider:</span>
              {guidance.secondary_actions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => handleActionClick(action)}
                  className="text-xs px-2 py-1 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] text-white/70 hover:text-gold hover:border-gold/50 transition-all duration-300 flex items-center gap-1"
                >
                  {action.label}
                  <ChevronRight className="w-3 h-3" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
